// ScrollIndicator.jsx
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// Bounce animation with reduced motion support
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(-50%);
  }
  40% {
    transform: translateY(-50%) translateY(-5px);
  }
  60% {
    transform: translateY(-50%) translateY(-3px);
  }
`;

const IndicatorWrapper = styled.button`
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  z-index: 1000;
  background: linear-gradient(90deg, #7b5cff, #60a5fa);
  color: white;
  padding: 12px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  outline: none;
  border: none;
  font-family: 'JetBrains Mono Regular';

  animation: ${bounce} 2s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &:focus {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.3),
      0 0 0 4px rgba(255, 255, 255, 0.3);
  }

  &:focus:not(:focus-visible) {
    outline: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.3),
      0 0 0 4px rgba(255, 255, 255, 0.3);
  }

  &:hover {
    cursor: pointer;
    transform: translateY(-50%) scale(1.05);
  }

  /* Ensure hover doesn't interfere with focus */
  &:focus:hover {
    transform: translateY(-50%) scale(1.05);
  }

  @media (max-width: 768px) {
    right: 10px;
    padding: 10px 12px;
    font-size: 12px;
  }

  min-width: 44px;
  min-height: 44px;
`;

const IndicatorText = styled.span`
  margin-bottom: 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const ArrowIcon = styled.svg`
  width: 20px;
  height: 20px;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

// Hidden text for screen readers only
const VisuallyHidden = styled.span`
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
`;

const ScrollIndicator = ({
  isVisible,
  onClick,
  text = 'More',
  showCount = false,
  ariaLabel,
  ariaDescribedBy,
  scrollDirection = 'down',
}) => {
  const buttonRef = useRef(null);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    };

    const button = buttonRef.current;
    if (button) {
      button.addEventListener('keydown', handleKeyDown);
      return () => button.removeEventListener('keydown', handleKeyDown);
    }
  }, [onClick]);

  if (!isVisible && !showCount) return null;

  // Generate appropriate ARIA label
  const getAriaLabel = () => {
    if (ariaLabel) return ariaLabel;
    const direction = scrollDirection === 'up' ? 'up' : 'down';
    const labelText = String(text || 'additional').toLowerCase();

    return `Scroll ${direction} to see ${labelText} content`;
  };

  // Generate appropriate aria-describedby content
  const getAriaDescription = () => {
    if (ariaDescribedBy) return ariaDescribedBy;
    return 'scroll-indicator-description';
  };

  return (
    <>
      {isVisible && (
        <>
          <IndicatorWrapper
            ref={buttonRef}
            onClick={onClick}
            role="button"
            tabIndex={0}
            aria-label={getAriaLabel()}
            aria-describedby={getAriaDescription()}
            aria-live="polite"
            aria-atomic="true"
          >
            <IndicatorText aria-hidden="true">{text}</IndicatorText>

            <ArrowIcon
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              role="img"
            >
              {scrollDirection === 'up' ? (
                <polyline points="18,15 12,9 6,15" />
              ) : (
                <polyline points="6,9 12,15 18,9" />
              )}
            </ArrowIcon>

            {/* Additional context for screen readers */}
            <VisuallyHidden>
              Press Enter or Space to scroll {scrollDirection === 'up' ? 'up' : 'down'}
            </VisuallyHidden>
          </IndicatorWrapper>

          {/* Description element for aria-describedby */}
          <div id={getAriaDescription()} style={{ display: 'none' }} aria-hidden="true">
            Scroll indicator button. Use keyboard navigation or click to scroll and view additional
            content.
          </div>
        </>
      )}
    </>
  );
};

export default ScrollIndicator;
